/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * --- REWRITE SUMMARY (V94.2 - Lint Fix) ---
 * - MISSION: To fix a `no-unused-vars` ESLint error.
 *
 * - THE FIX:
 * 1.  REMOVED HOOK: The `useModal` hook was being called and `openModal` was destructured from it.
 * 2.  REMOVED IMPORT: Since `openModal` was not used anywhere in the component, the entire line `const { openModal } = useModal();` has been removed.
 * 3.  CLEANUP: The corresponding import for `useModal` from `@/context/ModalContext` was also removed as it was no longer needed.
 *
 * - RESULT: The component is now free of the `no-unused-vars` linting error, ensuring a clean and successful build.
 */
import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useNavContext } from '@/context/NavigationProvider';
import { useUser } from '@/hooks/useUser';
import { getLibraryPath } from '@/utils/routes';
import LanguageSwitcher from './LanguageSwitcher';
import { FaBars, FaSearch, FaArrowLeft, FaTachometerAlt, FaRocket, FaUser } from 'react-icons/fa';
import { MdVideoLibrary } from 'react-icons/md';

const MobileMenu = lazy(() => import('./MobileMenu'));
const SearchResults = lazy(() => import('../shared/SearchResults'));

const Header = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const location = useLocation(); // <-- Get current location
  const searchContainerRef = useRef(null);
  const [isScrolledPastBanner, setIsScrolledPastBanner] = useState(false);

  const {
    isMenuOpen,
    setIsMenuOpen,
    searchQuery,
    handleSearchChange,
    clearSearch,
    navigateTo,
    isMobileSearchVisible,
    toggleMobileSearch,
    navigateToIndikinPass,
  } = useNavContext();



  // Effect to handle scroll position for button animation
  // Effect to handle scroll position for button animation using IntersectionObserver
  useEffect(() => {
    const targetId = 'hero-section';
    const fallbackTargetId = 'announcement-banner';
    const targetElement = document.getElementById(targetId) || document.getElementById(fallbackTargetId);

    // If no target element is found (e.g. on pages without hero), set scrolled to true immediately
    if (!targetElement) {
      setIsScrolledPastBanner(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // If the target is intersecting, we are NOT scrolled past it (or we are at top)
        // If it is NOT intersecting, and we are scrolled down, we are past it.
        // Actually, for "sticky" headers behavior, we typically want to know if the bottom of the hero
        // has passed the top of the viewport.

        // Simple logic: Is the element leaving the viewport at the top?
        // entry.isIntersecting is true when any part is visible.

        // Better logic for "Scrolled Past":
        // If entry.boundingClientRect.bottom < 0, then we are past it.
        // BUT IntersectionObserver is async.

        // Let's use the standard "sticky sentinel" trick or just check intersection ratio.
        // Or simpler: observe the target with threshold [0].
        // If !entry.isIntersecting && entry.boundingClientRect.top < 0 -> We are past it.

        if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
          setIsScrolledPastBanner(true);
        } else {
          // If we are intersecting, or if we are above it (top > 0), we are NOT past it.
          setIsScrolledPastBanner(false);
        }
      },
      {
        root: null, // viewport
        threshold: 0, // Trigger when even 1 pixel is visible/invisible
        rootMargin: "0px"
      }
    );

    observer.observe(targetElement);

    return () => {
      if (targetElement) observer.unobserve(targetElement);
    };
  }, []);

  // Effect to lock body scroll when mobile menus are open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen || isMobileSearchVisible ? 'hidden' : '';
  }, [isMenuOpen, isMobileSearchVisible]);

  // Effect to handle clicks outside of search to close results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchQuery && searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        clearSearch();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchQuery, clearSearch]);

  const handleMobileResultClick = (item) => {
    toggleMobileSearch();
    setTimeout(() => navigateTo(item), 300);
  };

  const handleGetPassClick = (e) => {
    e.preventDefault();
    navigateToIndikinPass();
  };

  return (
    <>
      <div className="header-container sticky top-0 z-[100]">
        <header className="flex items-center justify-between px-4 sm:px-6 py-4">
          {/* Left Side: Hamburger & Logo */}
          <div className="flex items-center flex-none">
            <button
              onClick={() => setIsMenuOpen(true)}
              className="p-2 ltr:mr-2 rtl:ml-2 text-white transition-colors md:hidden hover:text-primary"
              aria-label="Open menu"
            >
              <FaBars className="text-xl" />
            </button>
            <Link to="/" className="flex items-center logo">
              <img src="/assets/img/indikin-logo.png" alt="Indikin.online Logo" className="h-10 w-auto" width="188" height="40" />
            </Link>
          </div>

          {/* Center: Search Bar (Desktop) */}
          <div className="items-center justify-center flex-grow hidden px-4 md:flex">
            <div ref={searchContainerRef} className="relative w-full max-w-2xl">
              <FaSearch className="absolute top-1/2 ltr:left-4 rtl:right-4 transform -translate-y-1/2 text-white" />
              <input
                type="search"
                name="search"
                id="search-desktop"
                autoComplete="off"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={t('common.searchPlaceholder')}
                className="w-full py-2 ltr:pl-11 rtl:pr-11 ltr:pr-4 rtl:pl-4 text-white bg-search-bg border border-search-border rounded-full focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {searchQuery && <Suspense fallback={null}><SearchResults onResultClick={navigateTo} /></Suspense>}
            </div>
          </div>

          {/* Right Side: Buttons */}
          <div className="flex items-center flex-none ltr:pl-2 rtl:pr-2">
            {/* Desktop Buttons */}
            <div className="items-center hidden md:flex">
              <button
                onClick={handleGetPassClick}
                className={`btn btn-animate-scroll rounded-full flex items-center ltr:mr-4 rtl:ml-4 ${isScrolledPastBanner ? 'scrolled' : ''}`}
              >
                <span className="rocket-container ltr:mr-2 rtl:ml-2"><FaRocket className="-rotate-45" /></span>
                {t('common.getYourPass')}
              </button>

              {location.pathname === '/login' ? (
                <Link to={getLibraryPath(user)} className="btn rounded-full border border-gray-600 bg-transparent text-gray-300 hover:border-white hover:text-white transition-all ltr:mr-4 rtl:ml-4">
                  {t('header.nav.browseLibrary', 'Browse Library')}
                </Link>
              ) : user?.uid ? (
                <Link to={getLibraryPath(user)} className="btn btn-outline-primary rounded-full flex items-center ltr:mr-4 rtl:ml-4">
                  <FaTachometerAlt className="ltr:mr-2 rtl:ml-2" />
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link to={getLibraryPath(user)} className="btn rounded-full border border-gray-600 bg-transparent text-gray-300 hover:border-white hover:text-white transition-all ltr:mr-4 rtl:ml-4">
                    {t('header.nav.browseLibrary', 'Browse Library')}
                  </Link>
                  <Link to="/login" state={{ from: location.pathname }} id="login-signup-btn" className="btn btn-outline-primary rounded-full ltr:mr-4 rtl:ml-4">
                    {t('common.loginSignup', 'Login / Sign Up')}
                  </Link>
                </>
              )}
              <LanguageSwitcher />
            </div>

            {/* Mobile Buttons */}
            <button onClick={toggleMobileSearch} className="flex items-center justify-center w-10 h-10 text-white rounded-full md:hidden hover:bg-muted" aria-label="Open search">
              <FaSearch className="text-lg" />
            </button>
            <button onClick={handleGetPassClick} id="get-pass-btn-mobile" className={`flex items-center justify-center w-10 h-10 text-white rounded-full md:hidden btn btn-animate-scroll ${isScrolledPastBanner ? 'scrolled' : ''}`} aria-label={t('common.getYourPass')}>
              <span className="rocket-container"><FaRocket className="text-lg -rotate-45" /></span>
            </button>

            {location.pathname === '/login' ? (
              <Link to={getLibraryPath(user)} className="flex items-center justify-center w-10 h-10 text-white rounded-full md:hidden bg-transparent border border-gray-600 hover:border-white transition-all"
                aria-label={t('header.nav.browseLibrary', 'Browse Library')}
              >
                <MdVideoLibrary className="text-lg text-gray-300" />
              </Link>
            ) : user?.uid ? (
              <Link to={getLibraryPath(user)} className="flex items-center justify-center w-10 h-10 text-white rounded-full md:hidden border border-primary bg-primary/20 hover:bg-primary/40" aria-label="Dashboard">
                <FaTachometerAlt className="text-lg text-primary" />
              </Link>
            ) : (
              <Link to="/login" state={{ from: location.pathname }} id="login-signup-btn-mobile" className="flex items-center justify-center w-10 h-10 text-white rounded-full md:hidden border border-primary hover:bg-primary-20" aria-label={t('common.loginSignup', 'Login or Sign Up')}>
                <FaUser className="text-lg" />
              </Link>
            )}
          </div>
        </header>

      </div>

      {/* Mobile Menus */}
      {isMenuOpen && <Suspense fallback={null}><MobileMenu /></Suspense>}
      {
        isMobileSearchVisible && (
          <div className="fixed inset-0 bg-background z-[101] flex flex-col animate-slide-in-down" role="dialog" aria-modal="true">
            <div className="flex items-center p-2 border-b border-muted flex-shrink-0">
              <button onClick={() => { toggleMobileSearch(); clearSearch(); }} className="p-3 text-white rounded-full hover:bg-muted" aria-label="Close search">
                <FaArrowLeft className="text-xl" />
              </button>
              <div className="relative flex-grow">
                <input
                  id="mobile-search-input"
                  name="search-mobile"
                  type="search"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={t('common.searchPlaceholder')}
                  className="w-full py-2 pl-4 pr-4 text-white bg-transparent border-none rounded-full focus:outline-none"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex-grow overflow-y-auto">
              {searchQuery ? (
                <Suspense fallback={null}><SearchResults isMobileOverlay={true} onResultClick={handleMobileResultClick} /></Suspense>
              ) : (
                <div className="pt-10 text-center text-muted"><p>Search for storymakers and films.</p></div>
              )}
            </div>
          </div>
        )
      }
    </>
  );
};

export default Header;
